import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

export type BlogPostSummary = {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  cover_image_url: string | null;
  published_at: string | null;
  tags: string[];
};

const PLACEHOLDER_GRADIENTS = [
  "from-primary/25 to-primary/5",
  "from-red-300/30 to-primary/5",
  "from-secondary to-primary/10",
];

export const BlogCarousel = ({ posts }: { posts: BlogPostSummary[] }) => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = posts.length;
  const active = posts[index];

  useEffect(() => {
    if (paused || count <= 1) return;
    const timer = setInterval(() => setIndex((i) => (i + 1) % count), 6000);
    return () => clearInterval(timer);
  }, [paused, count]);

  useEffect(() => {
    setIndex((i) => Math.min(i, count - 1));
  }, [count]);

  if (!active) return null;

  return (
    <div
      className="relative overflow-hidden rounded-2xl bg-secondary group/carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <Link to={`/blog/${active.slug}`} className="block">
        <div className="grid md:grid-cols-2 items-stretch">
          <div className={`h-56 md:h-80 bg-gradient-to-br ${PLACEHOLDER_GRADIENTS[index % PLACEHOLDER_GRADIENTS.length]}`}>
            {active.cover_image_url && (
              <img
                src={active.cover_image_url}
                alt={active.title}
                loading="eager"
                decoding="async"
                className="w-full h-full object-cover"
              />
            )}
          </div>
          <div className="p-6 md:p-10 flex flex-col justify-center">
            <p className="text-primary-strong font-semibold text-xs uppercase tracking-widest mb-3">Featured</p>
            <h2 className="font-display text-xl md:text-2xl font-extrabold text-foreground group-hover/carousel:text-primary transition-colors">
              {active.title}
            </h2>
            {active.published_at && (
              <p className="text-xs text-muted-foreground mt-2">
                {new Date(active.published_at).toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
            )}
            {active.excerpt && (
              <p className="text-sm text-muted-foreground mt-4 leading-relaxed line-clamp-3">{active.excerpt}</p>
            )}
            <span className="inline-flex items-center gap-2 text-primary-strong font-semibold text-sm mt-6">
              Read the article <ArrowRight className="h-4 w-4" />
            </span>
          </div>
        </div>
      </Link>

      <button
        type="button"
        aria-label="Previous article"
        onClick={() => setIndex((i) => (i - 1 + count) % count)}
        className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-background/90 border border-border flex items-center justify-center text-foreground hover:text-primary hover:border-primary transition-colors"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button
        type="button"
        aria-label="Next article"
        onClick={() => setIndex((i) => (i + 1) % count)}
        className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-background/90 border border-border flex items-center justify-center text-foreground hover:text-primary hover:border-primary transition-colors"
      >
        <ChevronRight className="h-5 w-5" />
      </button>

      {count > 1 && (
        <div className="absolute bottom-4 left-6 md:left-auto md:right-10 flex gap-2">
          {posts.map((p, i) => (
            <button
              key={p.id}
              type="button"
              aria-label={`Go to article ${i + 1}`}
              onClick={() => setIndex(i)}
              className={`h-2 rounded-full transition-all ${i === index ? "w-6 bg-primary" : "w-2 bg-primary/30 hover:bg-primary/60"}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

type BlogCarouselSectionProps = {
  heading?: string;
  excludeSlug?: string;
};

/**
 * Fetches published posts and renders the carousel inside a page-width
 * section. Renders nothing when there are no published posts.
 */
const BlogCarouselSection = ({ heading = "From the blog", excludeSlug }: BlogCarouselSectionProps) => {
  const [posts, setPosts] = useState<BlogPostSummary[]>([]);

  useEffect(() => {
    supabase
      .from("blog_posts")
      .select("id, slug, title, excerpt, cover_image_url, published_at, tags")
      .eq("status", "published")
      .order("published_at", { ascending: false })
      .limit(10)
      .then(({ data }) => {
        const all = (data as BlogPostSummary[] | null) ?? [];
        const filtered = excludeSlug ? all.filter((p) => p.slug !== excludeSlug) : all;
        setPosts(filtered.length > 0 ? filtered : all);
      });
  }, [excludeSlug]);

  if (posts.length === 0) return null;

  return (
    <section className="py-14">
      <div className="container max-w-6xl mx-auto px-4">
        <div className="flex items-end justify-between mb-6">
          <h2 className="font-display text-2xl md:text-3xl font-extrabold text-foreground">{heading}</h2>
          <Link
            to="/blog"
            className="inline-flex items-center gap-1 text-primary-strong font-semibold text-sm hover:underline"
          >
            View all articles <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <BlogCarousel posts={posts} />
      </div>
    </section>
  );
};

export default BlogCarouselSection;
